/**
 * Tizen-specific kiosk helpers (isolated platform module — pattern análogo
 * ao `kioskMode.ts` que é Android-only). Lock de teclas perigosas do
 * controle Samsung + lock de botões físicos do painel SSSP.
 *
 * <p>No-op silencioso em outras plataformas — segue o mesmo contrato dos
 * outros adapters de plataforma. Não vaza pra outros stores/components;
 * apenas {@link KioskGuard} consome via {@link lockTizenKioskKeys} e
 * {@link setTizenPanelKeyLock}.</p>
 *
 * Decisão Renato 2026-06-01 (refinada v1.0.106 — 2026-06-01):
 *
 * <p><b>Estratégia v1.0.106</b>: durante kiosk locked, BLOQUEIA **TODAS** as
 * teclas (preventDefault + stopPropagation em capture phase) — vol, info,
 * enter, setas, exit, home, tudo. Sem exceções. Isso garante que:</p>
 * <ul>
 *   <li>VOL+/VOL- alimentam o detector MAS não alteram o volume real da TV</li>
 *   <li>INFO×7 desbloqueia MAS não abre o painel debug 7 vezes no meio</li>
 *   <li>Enter/setas inertes (exceto pra digitar PIN quando keyboard abre)</li>
 *   <li>Exit/Home/SmartHub/Source/Menu/etc inertes (não saem do app)</li>
 * </ul>
 *
 * <p><b>Ordem crítica</b>: o {@link KioskSequenceDetector} também usa
 * capture phase. KioskGuard registra o detector PRIMEIRO (mesma phase,
 * ordem de registro = ordem de execução). Detector lê a tecla, alimenta
 * sequência. Depois o blocker aqui executa preventDefault+stopPropagation,
 * sistema/app não veem a tecla.</p>
 *
 * <p>Refatoração v1.0.106 elimina a lista "KIOSK_PROTECTED_FROM_BLOCK" da
 * v1.0.105 — toda tecla é bloqueada, e o detector ainda funciona porque
 * roda ANTES no mesmo capture phase.</p>
 */

/**
 * Teclas Tizen que precisam ser **registradas** via `tvinputdevice.registerKey`
 * pra serem entregues ao WebView (algumas teclas perigosas o OS engole se
 * não forem registradas). Mesmo registradas, o blocker bloqueia o default.
 *
 * <p>VOL/Info/Enter NÃO precisam ser registradas — já chegam ao WebView por
 * padrão. Mas listamos VOL aqui pra garantir interceptação consistente.</p>
 */
const KIOSK_KEYS_TO_REGISTER = [
  'Exit',
  'XF86Back',
  'Smart Hub',
  'SmartHub',
  'Source',
  'Menu',
  'Tools',
  'PIP',
  'ChannelList',
  'Home',
  'XF86Home',
  'ChannelUp',
  'ChannelDown',
  'MediaTrackPrevious',
  'MediaTrackNext',
  'MediaPlayPause',
  // Números 1-6 — combo de desbloqueio pra SSSP. Na SSSP o botão OK/central vira
  // "mudo" (ruim/confuso de usar) e o volume/mudo é suprimido; os NÚMEROS são
  // inequívocos e existem em todo controle. MAS na SSSP eles são registráveis-porém-
  // -suprimidos: sem registerKey NÃO geram keydown no WebView (confirmado via CDP —
  // apertar 1-6 na SSSP travada não capturava nada). Registrar aqui entrega ao app
  // quando o kiosk trava → o detector vê a sequência. No unlock são desregistrados
  // (cleanup) → voltam ao handling nativo da TV.
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
];

interface TizenInputDevice {
  getSupportedKeys?: () => Array<{ name: string; code: number }>;
  registerKey?: (key: string) => void;
  unregisterKey?: (key: string) => void;
  registerKeyBatch?: (keys: string[]) => void;
  unregisterKeyBatch?: (keys: string[]) => void;
}

interface TizenB2BControl {
  setPanelKeyLock?: (lock: boolean) => void;
  getPanelKeyLock?: () => boolean;
  /** v1.0.109 — Bloqueia receiver IR (controle remoto inteiro). SSSP only. */
  setIRRemoteLock?: (lock: boolean) => void;
  getIRRemoteLock?: () => boolean;
  /** v1.0.109 — Bloqueia portas USB (proteção anti-tampering). SSSP only. */
  setUSBLockState?: (lock: boolean) => void;
  getUSBLockState?: () => boolean;
  /**
   * v1.0.109 — Agenda liga/desliga automático. Formato "HH:MM" (24h).
   * Passa string vazia pra desativar timer específico. SSSP only.
   */
  setOnTimer?: (time: string) => void;
  setOffTimer?: (time: string) => void;
  getOnTimer?: () => string;
  getOffTimer?: () => string;
  /** v1.0.109 — Reboot da TV. SSSP only. */
  rebootDevice?: () => void;
}

/** v1.0.109 — Package manager (substitui no-op em TizenPlatformService.installUpdate). */
interface TizenPackage {
  install?: (
    path: string,
    listener?: {
      onprogress?: (id: string, progress: number) => void;
      oncomplete?: (id: string) => void;
    },
  ) => void;
}

interface TizenWindow {
  tizen?: {
    tvinputdevice?: TizenInputDevice;
    package?: TizenPackage;
  };
  webapis?: {
    b2bapis?: {
      b2bcontrol?: TizenB2BControl;
    };
  };
}

const keysToRegister = (inputDevice: TizenInputDevice): string[] => {
  let supported: string[] = [];
  try {
    supported = (inputDevice.getSupportedKeys?.() ?? []).map((k) => k.name);
  } catch {
    // Firmware sem getSupportedKeys — fica só a lista fixa
  }
  return Array.from(new Set([...KIOSK_KEYS_TO_REGISTER, ...supported]));
};

const getTizenWindow = (): TizenWindow => window as unknown as TizenWindow;

const isTizenAvailable = (): boolean => {
  const platform = (
    window as unknown as { __effluxPlatform?: { platform: string } }
  ).__effluxPlatform;
  return platform?.platform === 'tizen';
};

/**
 * Registra teclas perigosas no Tizen + instala listener que faz
 * preventDefault. Retorna função de cleanup pra remover registros + listener.
 *
 * <p>No-op em outras plataformas. Idempotente — chamadas múltiplas reusam
 * o mesmo estado.</p>
 */
export function lockTizenKioskKeys(): () => void {
  if (!isTizenAvailable()) {
    return () => {
      /* no-op */
    };
  }
  const inputDevice = getTizenWindow().tizen?.tvinputdevice;
  if (!inputDevice) {
    console.warn('[TizenKiosk] tvinputdevice não disponível');
    return () => {
      /* no-op */
    };
  }

  // Registra TODAS as teclas que a TV declara (getSupportedKeys) + a lista fixa.
  // Spike na SSSP QB50B (2026-09-25): a TV declara 46 teclas e aceita registrar
  // todas; registradas + preventDefault, volume/mudo/números/cores/Source/Menu
  // chegam ao app e a TV NÃO age (volume não muda). Só Home e Power escapam —
  // nunca chegam ao WebView. Sem registro, volume/mudo/números são suprimidos
  // e a TV age sozinha.
  const registered: string[] = [];
  for (const key of keysToRegister(inputDevice)) {
    try {
      inputDevice.registerKey?.(key);
      registered.push(key);
    } catch {
      // Algumas teclas podem não existir no modelo da TV — silent
    }
  }
  console.log(
    `[TizenKiosk] 🔒 Registradas ${registered.length} teclas pra interceptação. Blocker em capture phase vai bloquear TODAS as teclas (preventDefault + stopPropagation).`,
  );

  // v1.0.106 — Blocker em capture phase bloqueia TODAS as teclas durante
  // kiosk locked. Sem exceções. O KioskSequenceDetector (capture phase,
  // registrado ANTES via attach()) já leu a tecla nesse momento — então a
  // sequência secreta funciona, mas o sistema/app NÃO veem a tecla.
  //
  // Resultado prático:
  // - VOL+/VOL-: detector conta, MAS volume da TV NÃO muda
  // - INFO: detector conta, MAS painel debug NÃO abre
  // - Enter/setas: detector vê, MAS app NÃO processa (exceto dentro do PIN
  //   keyboard que tem seu próprio handler em bubble phase)
  // - Exit/Home/SmartHub/Source: app continua, sistema NÃO sai do app
  const blocker = (e: KeyboardEvent) => {
    // Bloqueia DEFAULT (sistema vê) + PROPAGATION (handlers bubble não veem)
    e.preventDefault();
    e.stopPropagation();
    // Não loga toda tecla — só as "interessantes" pra debug ficar limpo
    if (
      ['VolumeUp', 'VolumeDown', 'Enter', 'Info', 'Exit', 'Home'].includes(
        e.key,
      )
    ) {
      console.log(`[TizenKiosk] 🚫 Bloqueado: ${e.key}`);
    }
  };
  // Capture phase pra interceptar antes do app
  window.addEventListener('keydown', blocker, true);

  return () => {
    window.removeEventListener('keydown', blocker, true);
    for (const key of registered) {
      try {
        inputDevice.unregisterKey?.(key);
      } catch {
        /* silent */
      }
    }
    console.log(`[TizenKiosk] 🔓 Teclas liberadas`);
  };
}

/**
 * SSSP only: bloqueia/libera botões físicos do painel da TV (POWER, VOL,
 * SOURCE, MENU do gabinete). Decisão Renato 2026-06-01 — combo no painel
 * é `SOURCE + VOL- por 3s` (implementação futura no Sprint K3).
 *
 * <p>Em Consumer Tizen ou outras plataformas: no-op silencioso. Best-effort
 * — se a API não responder, log warn mas não quebra.</p>
 */
export async function setTizenPanelKeyLock(lock: boolean): Promise<void> {
  if (!isTizenAvailable()) return;
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  if (!b2bcontrol?.setPanelKeyLock) {
    // Consumer Tizen ou Partner Cert ausente — esperado, vide L3/L11 das
    // limitações em FEATURE-KIOSK-MODE.MD
    console.log(
      `[TizenKiosk] setPanelKeyLock não disponível (Consumer ou sem Partner Cert)`,
    );
    return;
  }
  try {
    b2bcontrol.setPanelKeyLock(lock);
    console.log(`[TizenKiosk] 🔐 Panel key lock: ${lock}`);
  } catch (err) {
    console.warn('[TizenKiosk] setPanelKeyLock falhou:', err);
  }
}

/**
 * v1.0.109 — Resultado padronizado pras chamadas B2B. Todas degradam
 * graciosamente em Consumer / sem Partner Cert → retorna `unsupported`.
 */
export interface TizenB2BResult {
  supported: boolean;
  /** Mensagem humana — útil pro admin mostrar tooltip / error. */
  reason?: string;
}

const UNSUPPORTED_NO_PARTNER_CERT: TizenB2BResult = {
  supported: false,
  reason:
    'API B2B não disponível — requer TV SSSP Signage + Samsung Partner Certificate',
};

const UNSUPPORTED_NOT_TIZEN: TizenB2BResult = {
  supported: false,
  reason: 'API B2B só disponível em Tizen',
};

/**
 * SSSP only: bloqueia/libera USB ports (proteção anti-tampering — impede
 * pendrive de injetar conteúdo malicioso ou tentar firmware modificado).
 * Sprint K-Pre-Cert (Renato 2026-06-01).
 *
 * <p>Em Consumer / sem Partner Cert → no-op + retorna unsupported.</p>
 */
export async function setTizenUSBLock(lock: boolean): Promise<TizenB2BResult> {
  if (!isTizenAvailable()) return UNSUPPORTED_NOT_TIZEN;
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  if (!b2bcontrol?.setUSBLockState) {
    console.log(`[TizenKiosk] setUSBLockState não disponível`);
    return UNSUPPORTED_NO_PARTNER_CERT;
  }
  try {
    b2bcontrol.setUSBLockState(lock);
    console.log(`[TizenKiosk] 🔌 USB lock: ${lock}`);
    return { supported: true };
  } catch (err) {
    console.warn('[TizenKiosk] setUSBLockState falhou:', err);
    return { supported: false, reason: String(err) };
  }
}

/**
 * SSSP only: bloqueia receiver IR — **nenhuma tecla do controle remoto
 * chega à TV**. Anti-brick crítico: o admin precisa manter rota de
 * desbloqueio independente (admin remoto via SSE) antes de ativar.
 *
 * <p>Em Consumer / sem Partner Cert → no-op + retorna unsupported.</p>
 */
export async function setTizenIRRemoteLock(
  lock: boolean,
): Promise<TizenB2BResult> {
  if (!isTizenAvailable()) return UNSUPPORTED_NOT_TIZEN;
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  if (!b2bcontrol?.setIRRemoteLock) {
    console.log(`[TizenKiosk] setIRRemoteLock não disponível`);
    return UNSUPPORTED_NO_PARTNER_CERT;
  }
  try {
    b2bcontrol.setIRRemoteLock(lock);
    console.log(`[TizenKiosk] 📡 IR Remote lock: ${lock}`);
    return { supported: true };
  } catch (err) {
    console.warn('[TizenKiosk] setIRRemoteLock falhou:', err);
    return { supported: false, reason: String(err) };
  }
}

/**
 * SSSP only: agenda liga/desliga automático da TV. Formato "HH:MM" (24h).
 * Passa string vazia pra desativar timer específico. Vincula com horários
 * comerciais da loja (`tab_stores.opening_hours`) — Sprint K-Pre-Cert.
 *
 * <p>Em Consumer / sem Partner Cert → no-op + retorna unsupported.</p>
 */
export async function setTizenScheduledPower(
  onTime: string,
  offTime: string,
): Promise<TizenB2BResult> {
  if (!isTizenAvailable()) return UNSUPPORTED_NOT_TIZEN;
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  if (!b2bcontrol?.setOnTimer || !b2bcontrol?.setOffTimer) {
    console.log(`[TizenKiosk] setOn/OffTimer não disponíveis`);
    return UNSUPPORTED_NO_PARTNER_CERT;
  }
  try {
    b2bcontrol.setOnTimer(onTime);
    b2bcontrol.setOffTimer(offTime);
    console.log(`[TizenKiosk] ⏰ Scheduled power: on=${onTime} off=${offTime}`);
    return { supported: true };
  } catch (err) {
    console.warn('[TizenKiosk] setScheduledPower falhou:', err);
    return { supported: false, reason: String(err) };
  }
}

/**
 * SSSP only: reinicia a TV via API B2B. Substitui o no-op atual em
 * TizenPlatformService.restart (que só faz reload da WebView).
 *
 * <p>Em Consumer / sem Partner Cert → no-op + retorna unsupported.
 * Caller deve fallback pra `window.location.reload()` se quiser tentar
 * algo (reload do WebView cobre 95% dos casos em Consumer).</p>
 */
export async function rebootTizenDevice(): Promise<TizenB2BResult> {
  if (!isTizenAvailable()) return UNSUPPORTED_NOT_TIZEN;
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  if (!b2bcontrol?.rebootDevice) {
    console.log(`[TizenKiosk] rebootDevice não disponível`);
    return UNSUPPORTED_NO_PARTNER_CERT;
  }
  try {
    b2bcontrol.rebootDevice();
    console.log(`[TizenKiosk] 🔄 Reboot device disparado`);
    return { supported: true };
  } catch (err) {
    console.warn('[TizenKiosk] rebootDevice falhou:', err);
    return { supported: false, reason: String(err) };
  }
}

/**
 * SSSP only: instala WGT silenciosamente via `tizen.package.install`.
 * Requer privilege `packagemanager.install` (Partner Cert) + URL HTTPS
 * acessível da TV. Substitui o no-op atual em
 * TizenPlatformService.installUpdate.
 *
 * <p>Em Consumer / sem Partner Cert → no-op + retorna unsupported.
 * Operador precisa reinstalar manualmente via Tizen Studio.</p>
 *
 * @param wgtUrl URL HTTPS do WGT (ex: `https://api.efflux.media/wgt/efflux/1.0.110.wgt`)
 */
export async function installTizenPackage(
  wgtUrl: string,
): Promise<TizenB2BResult> {
  if (!isTizenAvailable()) return UNSUPPORTED_NOT_TIZEN;
  const pkg = getTizenWindow().tizen?.package;
  if (!pkg?.install) {
    console.log(`[TizenKiosk] tizen.package.install não disponível`);
    return UNSUPPORTED_NO_PARTNER_CERT;
  }
  try {
    pkg.install(wgtUrl, {
      onprogress: (id, progress) =>
        console.log(`[TizenKiosk] 📦 install ${id} progress: ${progress}%`),
      oncomplete: (id) => console.log(`[TizenKiosk] ✅ install ${id} completo`),
    });
    console.log(`[TizenKiosk] 📥 Install disparado: ${wgtUrl}`);
    return { supported: true };
  } catch (err) {
    console.warn('[TizenKiosk] tizen.package.install falhou:', err);
    return { supported: false, reason: String(err) };
  }
}

/**
 * Probe binário — checa se Partner Cert está ativo (Tizen SSSP com
 * privileges B2B destravados). Heurística: tenta ler `setPanelKeyLock`
 * — se existe na superfície da API, cert está OK.
 *
 * <p>Usado pelo admin pra renderizar tooltips "Aguardando Partner Cert"
 * vs controles ativos.</p>
 */
export function isTizenPartnerCertAvailable(): boolean {
  if (!isTizenAvailable()) return false;
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  return typeof b2bcontrol?.setPanelKeyLock === 'function';
}

/**
 * v1.0.110 — Resumo das capabilities Tizen pra debug + admin RemoteOps
 * `kiosk:get-state`. Roda 1x no boot (lazy) e cacheia. Útil pra equipe
 * de campo entender o que tá disponível antes de configurar kiosk.
 */
export interface TizenKioskCapabilities {
  /** 'consumer' | 'signage' | 'unknown' — vide capturePlatform.ts */
  deviceClass: string;
  /** Modelo Samsung detectado (DUID-based) — ex: 'UN50U8800FGXZD'. */
  model: string | null;
  /** Partner Cert ativo? Se false, APIs B2B não estão disponíveis. */
  partnerCertActive: boolean;
  /** Lista de APIs B2B disponíveis no momento. */
  availableB2bMethods: string[];
}

let cachedCaps: TizenKioskCapabilities | null = null;

export async function getTizenKioskCapabilities(): Promise<TizenKioskCapabilities> {
  if (cachedCaps) return cachedCaps;
  if (!isTizenAvailable()) {
    cachedCaps = {
      deviceClass: 'non-tizen',
      model: null,
      partnerCertActive: false,
      availableB2bMethods: [],
    };
    return cachedCaps;
  }
  // Import dinâmico pra evitar dep cíclica em testes
  const { detectTizenDeviceClass } = await import('~/utils/capturePlatform');
  const b2bcontrol = getTizenWindow().webapis?.b2bapis?.b2bcontrol;
  const pkg = getTizenWindow().tizen?.package;
  const available: string[] = [];
  if (b2bcontrol?.setPanelKeyLock) available.push('setPanelKeyLock');
  if (b2bcontrol?.setUSBLockState) available.push('setUSBLockState');
  if (b2bcontrol?.setIRRemoteLock) available.push('setIRRemoteLock');
  if (b2bcontrol?.setOnTimer) available.push('setOnTimer');
  if (b2bcontrol?.setOffTimer) available.push('setOffTimer');
  if (b2bcontrol?.rebootDevice) available.push('rebootDevice');
  if (pkg?.install) available.push('tizen.package.install');

  cachedCaps = {
    deviceClass: detectTizenDeviceClass(),
    model: null, // resolvido async em getTizenModel — não bloqueia aqui
    partnerCertActive: isTizenPartnerCertAvailable(),
    availableB2bMethods: available,
  };
  console.log(
    `[TizenKiosk] 📋 Capabilities: class=${cachedCaps.deviceClass}, partnerCert=${cachedCaps.partnerCertActive}, B2B methods=${available.length}/${7} (${available.join(', ') || 'nenhum'})`,
  );
  return cachedCaps;
}
